import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Users, 
  Clock, 
  MapPin, 
  Layers, 
  ArrowLeft, 
  Edit2, 
  Trash2,
  Search,
  Plus,
  X,
  CheckCircle2
} from 'lucide-react';

// Day colors matching the reference design
const dayColors = {
  Sunday: { header: 'bg-purple-600', card: 'bg-purple-100', border: 'border-purple-300' },
  Monday: { header: 'bg-blue-600', card: 'bg-blue-100', border: 'border-blue-300' },
  Tuesday: { header: 'bg-teal-600', card: 'bg-teal-100', border: 'border-teal-300' },
  Wednesday: { header: 'bg-amber-700', card: 'bg-amber-100', border: 'border-amber-300' },
  Thursday: { header: 'bg-rose-600', card: 'bg-rose-100', border: 'border-rose-300' },
  Friday: { header: 'bg-indigo-600', card: 'bg-indigo-100', border: 'border-indigo-300' },
  Saturday: { header: 'bg-emerald-600', card: 'bg-emerald-100', border: 'border-emerald-300' },
};

// Group colors
const groupColors = {
  Blue: 'bg-blue-500 text-white',
  Green: 'bg-green-500 text-white',
  Red: 'bg-red-500 text-white',
  Yellow: 'bg-yellow-500 text-black',
  Purple: 'bg-purple-500 text-white',
  Orange: 'bg-orange-500 text-white',
};

// Skill badge colors
const skillBadgeColors = {
  'Walkie': 'bg-blue-100 text-blue-700 border-blue-200',
  'Experienced Counter': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'New Starter': 'bg-pink-100 text-pink-700 border-pink-200',
  'Paint Mixing': 'bg-purple-100 text-purple-700 border-purple-200',
  'Team Leader': 'bg-green-100 text-green-700 border-green-200',
  'Forklift': 'bg-orange-100 text-orange-700 border-orange-200',
};

const RosterAreas = () => {
  const { user } = useContext(AuthContext);
  const [view, setView] = useState('days'); // 'days' or 'roster'
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeTab, setActiveTab] = useState('roster'); // 'roster', 'groups', 'areas'
  const [rosters, setRosters] = useState([]);
  const [users, setUsers] = useState([]);
  const [stocktakes, setStocktakes] = useState([]);
  const [countAreas, setCountAreas] = useState([]);
  const [areaAssignments, setAreaAssignments] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [assignAreaModalOpen, setAssignAreaModalOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    user_name: '',
    team_member_number: '',
    home_store: '',
    start_time: '16:30',
    end_time: '23:00',
    is_backup: false,
    group: 'Blue',
    skills: []
  });
  const token = localStorage.getItem('token');

  // Get the current store_id (for Network Admin, check localStorage)
  const getStoreId = () => {
    if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
      return localStorage.getItem('selectedStoreId') || user?.store_id;
    }
    return user?.store_id;
  };

  const [currentStoreId, setCurrentStoreId] = useState(getStoreId());
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];

  // Re-fetch when store changes
  useEffect(() => {
    const newStoreId = getStoreId();
    if (newStoreId !== currentStoreId) {
      setCurrentStoreId(newStoreId);
    }
    fetchData();
  }, [user?.store_id]);

  // Listen for store changes from localStorage (for admin store switching)
  useEffect(() => {
    const handleStorageChange = () => {
      const newStoreId = getStoreId();
      if (newStoreId !== currentStoreId) {
        setCurrentStoreId(newStoreId);
        fetchData();
      }
    };
    
    const handleStoreChanged = (event) => {
      const newStoreId = event.detail?.storeId;
      if (newStoreId && newStoreId !== currentStoreId) {
        setCurrentStoreId(newStoreId);
        fetchData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleStorageChange);
    window.addEventListener('storeChanged', handleStoreChanged);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
      window.removeEventListener('storeChanged', handleStoreChanged);
    };
  }, [currentStoreId]);

  const fetchData = async () => {
    try {
      const storeId = getStoreId();
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      
      // Fetch ALL data with store filter - each store has its own data
      const [rostersRes, usersRes, stocktakesRes, countAreasRes, areaAssignmentsRes] = await Promise.all([
        fetch(`${API}/rosters${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/users${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/stocktakes${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/count-areas${storeParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/area-assignments${storeParam}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRosters(await rostersRes.json());
      setUsers(await usersRes.json());
      setStocktakes(await stocktakesRes.json());
      if (countAreasRes.ok) setCountAreas(await countAreasRes.json());
      if (areaAssignmentsRes.ok) setAreaAssignments(await areaAssignmentsRes.json());
    } catch (error) {
      console.error(error);
    }
  };

  // Get roster data for a specific day
  const getDayData = (day) => {
    const dayRosters = rosters.filter(r => r.day === day);
    const groups = [...new Set(dayRosters.map(r => r.group).filter(Boolean))];
    const dayCountAreas = countAreas.filter(a => a.day === day);
    const dayAssignments = areaAssignments.filter(a => a.day === day);
    const totalHours = dayRosters.reduce((sum, r) => {
      if (r.shift_start && r.shift_end) {
        const start = new Date(`2000-01-01T${r.start_time || '16:30'}`);
        const end = new Date(`2000-01-01T${r.end_time || '23:00'}`);
        return sum + (end - start) / (1000 * 60 * 60);
      }
      return sum + 6.5; // Default 6.5 hours
    }, 0);
    
    return {
      people: dayRosters.length,
      groups: groups.length || 0,
      areas: dayCountAreas.length,
      assignedAreas: dayAssignments.length,
      hours: totalHours.toFixed(1),
      rosters: dayRosters,
      countAreas: dayCountAreas,
      assignments: dayAssignments
    };
  };

  // Get assignment for a specific area
  const getAreaAssignment = (areaId) => {
    return areaAssignments.find(a => a.area_id === areaId);
  };

  // Handle area assignment
  const handleAssignArea = async (rosterId, rosterName) => {
    if (!selectedArea) return;
    
    const storeId = getStoreId();
    const activeStocktake = stocktakes.find(st => st.status === 'planning' || st.status === 'scheduled' || st.status === 'in_progress') || stocktakes[0];
    
    try {
      const response = await fetch(`${API}/area-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          area_id: selectedArea.id,
          area_name: selectedArea.name,
          day: selectedDay,
          roster_id: rosterId,
          assigned_to_name: rosterName,
          store_id: storeId,
          stocktake_id: activeStocktake?.id
        })
      });
      
      if (response.ok) {
        toast.success(`${selectedArea.name} assigned to ${rosterName}`);
        setAssignAreaModalOpen(false);
        setSelectedArea(null);
        fetchData();
      } else {
        toast.error('Failed to assign area');
      }
    } catch (error) {
      toast.error('Failed to assign area');
    }
  };

  // Remove area assignment
  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await fetch(`${API}/area-assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Assignment removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  // Update assignment status
  const handleUpdateAssignmentStatus = async (assignmentId, status) => {
    try {
      await fetch(`${API}/area-assignments/${assignmentId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setView('roster');
  };

  const handleBackToCards = () => {
    setView('days');
    setSelectedDay(null);
  };

  const handleAddMember = async () => {
    if (!formData.user_name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    // Find any active stocktake - prioritize planning/scheduled/in_progress, but accept any
    let activeStocktake = stocktakes.find(st => 
      st.status === 'planning' || st.status === 'scheduled' || st.status === 'in_progress'
    );
    
    // Fallback to the first stocktake if no active one found
    if (!activeStocktake && stocktakes.length > 0) {
      activeStocktake = stocktakes[0];
    }
    
    if (!activeStocktake) {
      toast.error('No stocktake found. Please create a stocktake first in Scheduling.');
      return;
    }

    try {
      const storeId = getStoreId();
      const response = await fetch(`${API}/rosters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          stocktake_id: activeStocktake.id,
          store_id: storeId,
          user_name: formData.user_name.trim(),
          team_member_number: formData.team_member_number.trim() || null,
          home_store: formData.home_store.trim() || null,
          day: selectedDay,
          start_time: formData.start_time,
          end_time: formData.end_time,
          is_backup: formData.is_backup,
          group: formData.group,
          skills: formData.skills,
          area_assignment: ''
        })
      });
      
      if (response.ok) {
        toast.success(`${formData.user_name} added to ${selectedDay} roster`);
        setAddModalOpen(false);
        setFormData({
          user_name: '',
          team_member_number: '',
          home_store: '',
          start_time: '16:30',
          end_time: '23:00',
          is_backup: false,
          group: 'Blue',
          skills: []
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add team member');
      }
    } catch (error) {
      toast.error('Failed to add team member');
    }
  };

  const handleEditMember = async () => {
    if (!editingMember) return;

    try {
      const response = await fetch(`${API}/rosters/${editingMember.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          start_time: editingMember.start_time,
          end_time: editingMember.end_time,
          is_backup: editingMember.is_backup,
          group: editingMember.group,
          skills: editingMember.skills
        })
      });
      
      if (response.ok) {
        toast.success('Team member updated');
        setEditModalOpen(false);
        setEditingMember(null);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update team member');
    }
  };

  const handleDeleteMember = async (rosterId) => {
    try {
      await fetch(`${API}/rosters/${rosterId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removed from roster');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const dayData = selectedDay ? getDayData(selectedDay) : null;

  // Days Cards View
  if (view === 'days') {
    return (
      <div className="space-y-6" data-testid="roster-areas-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">Team Roster</h1>
            <p className="text-slate-600">Plan your stocktake team</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search..." 
                className="pl-10 w-48"
                data-testid="roster-search"
              />
            </div>
          </div>
        </div>

        {/* Day Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {days.map((day) => {
            const data = getDayData(day);
            const colors = dayColors[day];
            const isSelected = data.people > 0;
            
            return (
              <Card 
                key={day}
                className={`overflow-hidden cursor-pointer transition-all hover:shadow-lg ${isSelected ? 'ring-2 ring-offset-2 ring-teal-500' : ''}`}
                onClick={() => handleDayClick(day)}
                data-testid={`day-card-${day.toLowerCase()}`}
              >
                {/* Day Header */}
                <div className={`${colors.header} px-4 py-3 flex items-center justify-between`}>
                  <span className="text-white font-semibold">{day}</span>
                  {isSelected && (
                    <CheckCircle2 className="w-5 h-5 text-white/80" />
                  )}
                </div>
                
                {/* Stats Section */}
                <div className={`${colors.card} px-4 py-4`}>
                  <div className="flex items-center justify-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span className="font-semibold">{data.people}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Layers className="w-4 h-4" />
                      <span className="font-semibold">{data.groups}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span className="font-semibold">{data.areas}</span>
                    </div>
                  </div>
                </div>

                {/* Hours Section */}
                <div className={`${colors.card} px-4 py-3 border-t ${colors.border}`}>
                  <div className="flex items-center justify-center gap-1 text-sm">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">{data.hours}h</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Summary Stats */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Weekly Overview</h3>
          <div className="grid grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-teal-600">{rosters.length}</div>
              <div className="text-sm text-slate-600">Total Assignments</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {[...new Set(rosters.map(r => r.group).filter(Boolean))].length}
              </div>
              <div className="text-sm text-slate-600">Groups</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {[...new Set(rosters.map(r => r.area_assignment).filter(Boolean))].length}
              </div>
              <div className="text-sm text-slate-600">Areas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-600">
                {days.reduce((sum, day) => sum + parseFloat(getDayData(day).hours), 0).toFixed(1)}h
              </div>
              <div className="text-sm text-slate-600">Total Hours</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Day Roster Detail View
  return (
    <div className="space-y-6" data-testid="roster-detail-page">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleBackToCards}
          data-testid="back-to-days"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('roster')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'roster' 
              ? 'border-teal-600 text-teal-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-roster"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Roster
            <Badge variant="secondary" className="ml-1">{dayData?.people || 0}</Badge>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('groups')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'groups' 
              ? 'border-teal-600 text-teal-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-groups"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Groups
            <Badge variant="secondary" className="ml-1">{dayData?.groups || 0}</Badge>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('areas')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'areas' 
              ? 'border-teal-600 text-teal-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-areas"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Areas
            <Badge variant="secondary" className="ml-1">{dayData?.areas || 0}</Badge>
          </div>
        </button>
      </div>

      {/* Day Roster Header */}
      <div className={`${dayColors[selectedDay]?.header} rounded-xl px-6 py-4 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-white" />
          <span className="text-white font-semibold text-lg">{selectedDay} Roster</span>
        </div>
        <Badge className="bg-white/20 text-white border-0">
          {dayData?.people || 0} members
        </Badge>
      </div>

      {/* Roster Table */}
      {activeTab === 'roster' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="w-10 px-4 py-3"></th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Name</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">TM Number</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Home Store</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Time</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Group</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dayData?.rosters.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    No team members assigned to this day yet
                  </td>
                </tr>
              ) : (
                dayData?.rosters.map((member, idx) => {
                  return (
                    <tr 
                      key={member.id} 
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`roster-row-${member.id}`}
                    >
                      <td className="px-4 py-4">
                        <Checkbox />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium">
                            {member.user_name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{member.user_name}</div>
                            {member.is_backup && (
                              <span className="text-xs text-amber-600 font-medium">BACKUP</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-slate-600 font-mono">
                          {member.team_member_number || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-slate-600">
                          {member.home_store || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Clock className="w-4 h-4" />
                          {member.start_time || '16:30'} - {member.end_time || '23:00'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <Badge className={groupColors[member.group] || 'bg-blue-500 text-white'}>
                          {member.group || 'Blue'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setEditingMember(member);
                              setEditModalOpen(true);
                            }}
                            data-testid={`edit-member-${member.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteMember(member.id)}
                            data-testid={`delete-member-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Add Team Member Button at Bottom */}
          <div className="border-t border-slate-200 p-4">
            <Button 
              onClick={() => setAddModalOpen(true)}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="add-team-member-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </div>
        </div>
      )}

      {/* Groups Tab Content */}
      {activeTab === 'groups' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.keys(groupColors).map(group => {
              const groupMembers = dayData?.rosters.filter(r => r.group === group) || [];
              return (
                <Card key={group} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className={groupColors[group]}>{group}</Badge>
                    <span className="text-sm font-medium text-slate-600">{groupMembers.length} members</span>
                  </div>
                  <div className="space-y-2">
                    {groupMembers.map(m => (
                      <div key={m.id} className="text-sm text-slate-700">{m.user_name}</div>
                    ))}
                    {groupMembers.length === 0 && (
                      <div className="text-sm text-slate-400">No members assigned</div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Areas Tab Content */}
      {activeTab === 'areas' && (
        <div className="space-y-4">
          {/* Areas Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Count Areas for {selectedDay}</h3>
              <p className="text-sm text-slate-500">
                {dayData.assignedAreas} of {dayData.areas} areas assigned
              </p>
            </div>
          </div>

          {/* Areas List */}
          {dayData.countAreas.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No count areas configured for {selectedDay}.</p>
              <p className="text-sm text-slate-400 mt-1">Add count areas in Settings &gt; Count Areas</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className={`${dayColors[selectedDay]?.header || 'bg-slate-600'} text-white`}>
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Area Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Assigned To</th>
                    <th className="text-center py-3 px-4 font-semibold text-sm">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {dayData.countAreas.sort((a, b) => a.sort_order - b.sort_order).map((area) => {
                    const assignment = getAreaAssignment(area.id);
                    return (
                      <tr key={area.id} className="hover:bg-slate-50" data-testid={`area-row-${area.id}`}>
                        <td className="py-3 px-4">
                          <div className="font-medium text-slate-900">{area.name}</div>
                          {area.description && (
                            <div className="text-xs text-slate-500">{area.description}</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {assignment ? (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-semibold text-sm">
                                {assignment.assigned_to_name?.charAt(0) || '?'}
                              </div>
                              <span className="text-slate-700">{assignment.assigned_to_name}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">Unassigned</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {assignment ? (
                            <Badge 
                              variant="outline"
                              className={
                                assignment.status === 'complete' 
                                  ? 'bg-green-100 text-green-700 border-green-300' 
                                  : assignment.status === 'in_progress'
                                  ? 'bg-amber-100 text-amber-700 border-amber-300'
                                  : 'bg-blue-100 text-blue-700 border-blue-300'
                              }
                            >
                              {assignment.status === 'complete' ? 'Complete' : assignment.status === 'in_progress' ? 'In Progress' : 'Assigned'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-slate-100 text-slate-500">
                              Pending
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {!assignment ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-teal-600 border-teal-300 hover:bg-teal-50"
                                onClick={() => {
                                  setSelectedArea(area);
                                  setAssignAreaModalOpen(true);
                                }}
                                data-testid={`assign-area-${area.id}`}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Assign
                              </Button>
                            ) : (
                              <>
                                {assignment.status !== 'complete' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 border-green-300 hover:bg-green-50"
                                    onClick={() => handleUpdateAssignmentStatus(assignment.id, 'complete')}
                                    data-testid={`complete-${area.id}`}
                                  >
                                    <CheckCircle2 className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-500 border-red-200 hover:bg-red-50"
                                  onClick={() => handleRemoveAssignment(assignment.id)}
                                  data-testid={`remove-${area.id}`}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Assign Area Modal */}
      <Dialog open={assignAreaModalOpen} onOpenChange={setAssignAreaModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="assign-area-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Assign Area</DialogTitle>
            <p className="text-slate-500 text-sm">
              Assign "{selectedArea?.name}" to a team member rostered on {selectedDay}
            </p>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {dayData.rosters.length === 0 ? (
              <div className="text-center py-4">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No team members rostered on {selectedDay}</p>
                <p className="text-sm text-slate-400">Add team members to the roster first</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dayData.rosters.map((roster) => (
                  <button
                    key={roster.id}
                    className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-teal-50 hover:border-teal-300 transition-colors"
                    onClick={() => handleAssignArea(roster.id, roster.user_name)}
                    data-testid={`select-roster-${roster.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${groupColors[roster.group] || 'bg-slate-400 text-white'} flex items-center justify-center font-semibold`}>
                        {roster.user_name?.charAt(0) || '?'}
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-slate-900">{roster.user_name}</div>
                        <div className="text-xs text-slate-500">
                          {roster.start_time} - {roster.end_time} • {roster.group || 'No group'}
                        </div>
                      </div>
                    </div>
                    <Plus className="w-5 h-5 text-teal-600" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Team Member Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-member-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">ADD TEAM MEMBER</DialogTitle>
            <p className="text-slate-500 text-sm">Add a team member to {selectedDay?.toLowerCase()}'s roster</p>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Name Input */}
            <div>
              <Label className="text-sm font-semibold">Name *</Label>
              <Input
                placeholder="Enter team member's name"
                value={formData.user_name}
                onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                className="mt-2 border-teal-500 focus:ring-teal-500"
                data-testid="name-input"
              />
            </div>

            {/* Team Member Number */}
            <div>
              <Label className="text-sm font-semibold">Team Member Number</Label>
              <Input
                placeholder="Enter team member number (e.g., 424006)"
                value={formData.team_member_number}
                onChange={(e) => setFormData({ ...formData, team_member_number: e.target.value })}
                className="mt-2"
                data-testid="team-member-number-input"
              />
            </div>

            {/* Home Store */}
            <div>
              <Label className="text-sm font-semibold">Home Store</Label>
              <Input
                placeholder="Enter home store name"
                value={formData.home_store}
                onChange={(e) => setFormData({ ...formData, home_store: e.target.value })}
                className="mt-2"
                data-testid="home-store-input"
              />
            </div>

            {/* Time Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold">Start Time</Label>
                <Select 
                  value={formData.start_time} 
                  onValueChange={(val) => setFormData({ ...formData, start_time: val })}
                >
                  <SelectTrigger className="mt-2" data-testid="start-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00'].map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">End Time</Label>
                <Select 
                  value={formData.end_time} 
                  onValueChange={(val) => setFormData({ ...formData, end_time: val })}
                >
                  <SelectTrigger className="mt-2" data-testid="end-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00'].map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Backup Checkbox */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="backup"
                checked={formData.is_backup}
                onCheckedChange={(checked) => setFormData({ ...formData, is_backup: checked })}
                data-testid="backup-checkbox"
              />
              <Label htmlFor="backup" className="text-sm cursor-pointer">
                Mark as backup (standby)
              </Label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <Button 
              variant="outline" 
              onClick={() => setAddModalOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddMember}
              className="flex-1 bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-add-member"
            >
              Add Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Member Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          
          {editingMember && (
            <div className="space-y-4 py-4">
              <div className="font-medium text-lg">{editingMember.user_name}</div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Time</Label>
                  <Select 
                    value={editingMember.start_time || '16:30'} 
                    onValueChange={(val) => setEditingMember({ ...editingMember, start_time: val })}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00'].map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>End Time</Label>
                  <Select 
                    value={editingMember.end_time || '23:00'} 
                    onValueChange={(val) => setEditingMember({ ...editingMember, end_time: val })}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00'].map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Group</Label>
                <Select 
                  value={editingMember.group || 'Blue'} 
                  onValueChange={(val) => setEditingMember({ ...editingMember, group: val })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(groupColors).map(group => (
                      <SelectItem key={group} value={group}>{group}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  checked={editingMember.is_backup}
                  onCheckedChange={(checked) => setEditingMember({ ...editingMember, is_backup: checked })}
                />
                <Label className="text-sm cursor-pointer">Mark as backup (standby)</Label>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleEditMember} className="flex-1 bg-teal-600 hover:bg-teal-700">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RosterAreas;
